// TODO: Remove this es lint disable, it smells in here :D

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * TODO: Fix this file
 * Elastic Datatable Component
 *
 * NOTE: We're using local type definitions instead of importing from '@elastic/elasticsearch/lib/api/types'
 * due to TypeScript module resolution issues. When the module resolution is fixed, we can switch back to
 * direct imports from the Elasticsearch package.
 */
//import type {Indices, QueryDslQueryContainer, SearchRequest} from '@elastic/elasticsearch/lib/api/types';
import {ElasticDatabase, ElasticTable} from '@tnc/shared';
import clsx from 'clsx';
import React, {useCallback, useImperativeHandle, useMemo, useRef, useState} from 'react';
import ReactPaginate from 'react-paginate';
import Select from 'react-select';
import {twMerge} from 'tailwind-merge';

import {ReactPaginateClickEventT, VALID_LIMITS, limitT} from '@/lib/types/pagination.types';

import {useElasticQuery} from '@/hooks/useElasticQuery';

import {LoadingSpinningLogoComponent} from './loadingSpinningLogoComponent';

// Placeholder types for Elasticsearch (until module resolution is fixed)
// These match the types from @elastic/elasticsearch/lib/api/types
// Define only what we need for this component
type Indices = string;
type QueryDslQueryContainer = Record<string, any>;
type SearchRequest = {
  index: Indices;
  query?: QueryDslQueryContainer;
  sort?: Array<Record<string, {order: 'asc' | 'desc'}>>;
  // Add other properties as needed
};

// Types
export type ElasticDataTableRefT = {
  refetch: () => void;
  setLimit: (limit: limitT) => void;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
};

// Add new type helpers for dot notation with a depth limit
type DotNotationDepth1<T> = {
  [K in keyof T]: `${K & string}`;
}[keyof T];

type DotNotationDepth2<T> = {
  [K in keyof T]: T[K] extends object
    ? `${K & string}` | `${K & string}.${DotNotationDepth1<T[K]> & string}`
    : `${K & string}`;
}[keyof T];

type DotNotationDepth3<T> = {
  [K in keyof T]: T[K] extends object
    ? `${K & string}` | `${K & string}.${DotNotationDepth2<T[K]> & string}`
    : `${K & string}`;
}[keyof T];

// Use a reasonable depth limit to avoid type instantiation issues
type DotNotation<T> = DotNotationDepth3<T>;

// Update ElasticColumnT to use dot notation
export type ElasticColumnT<T> = {
  field: DotNotation<T>;
  header: string;
  render?: (row: T) => React.ReactNode;
};

export type ElasticActionT<T> = {
  icon: string;
  color: string;
  handler: (row: T) => void;
  permission: (row: T) => boolean;
  isShown?: (row: T) => boolean;
};

export type ElasticSortOptionT = {
  field: string;
  direction: 'asc' | 'desc';
  label: string;
  default?: boolean;
};

// Types for filter configuration
export type ElasticFilterType = 'select' | 'text' | 'number' | 'multiple-select';

export type ElasticFilterOption = {
  label: string;
  value: string;
};

export type ElasticFilterConfig = {
  field: string;
  label: string;
  type: ElasticFilterType;
  options?: ElasticFilterOption[];
  placeholder?: string;
  useTypeahead?: boolean;
  minChars?: number;
  aggregationField?: string;
  defaultValue?: string | (string | null)[] | undefined;
};

export type ElasticDataTablePropsT<T extends ElasticTable> = {
  // Data
  index: T;
  columns: ElasticColumnT<ElasticDatabase[T]>[];
  baseQuery?: QueryDslQueryContainer;
  // Options
  actions?: ElasticActionT<ElasticDatabase[T]>[];
  limit?: limitT;
  sortOptions?: ElasticSortOptionT[];
  searchColumn?: keyof ElasticDatabase[T];
  enabled?: boolean;
  // Display
  title?: string | ((data: {aggregations?: Record<string, any>}) => React.ReactNode);
  className?: string;
  addButtonLabel?: string;
  // Display Optionals
  hideSearch?: boolean;
  hideRefresh?: boolean;
  hideSort?: boolean;
  hideTitle?: boolean;
  hideAdd?: boolean;
  hideShowing?: boolean;
  hideLimit?: boolean;
  hideLimitIcon?: boolean;
  hidePagination?: boolean;
  hideLoading?: boolean;
  noResultsMessage?: string;
  // Functions
  onRefresh?: () => void;
  onSearch?: (value: string) => void;
  onAdd?: () => void;
  onSort?: (option: ElasticSortOptionT) => void;
  onSetLimit?: (limit: limitT) => void;
  onGoToPage?: (page: number) => void;
  filters?: ElasticFilterConfig[];
  onFilterChange?: (filters: Record<string, any>) => void;
  aggs?: Record<string, any>;
};

// Helper function to get nested value
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), obj);
}

function ElasticDataTableComponent<T extends ElasticTable>(
  props: ElasticDataTablePropsT<T>,
  ref: React.ForwardedRef<ElasticDataTableRefT>
) {
  const {
    index,
    columns,
    baseQuery,
    actions,
    limit: initialLimit = 10,
    sortOptions = [],
    searchColumn,
    enabled = true,
    title,
    className,
    hideSearch = false,
    hideRefresh = false,
    hideSort = false,
    hideTitle = false,
    hideAdd = false,
    hideShowing = false,
    hideLimit = false,
    hideLimitIcon = false,
    hidePagination = false,
    hideLoading = false,
    noResultsMessage = 'No results found',
    addButtonLabel = 'Create New',
    onRefresh,
    onAdd,
    onSort,
    filters = [],
    onFilterChange,
  } = props;

  // State
  const [page, setPage] = useState(1);
  const [limit, setLocalLimit] = useState<limitT>(initialLimit);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSort, setSelectedSort] = useState<ElasticSortOptionT | null>(
    sortOptions.find(opt => opt.default) ?? null
  );

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null!);

  // State for filters
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
    const initialFilters: Record<string, any> = {};
    filters.forEach(filter => {
      if (filter.defaultValue !== undefined && enabled) {
        initialFilters[filter.field] = filter.defaultValue;
      }
    });
    return initialFilters;
  });

  // Add useEffect to update filters when enabled changes
  React.useEffect(() => {
    if (enabled) {
      const updatedFilters: Record<string, any> = {};
      filters.forEach(filter => {
        if (filter.defaultValue !== undefined) {
          updatedFilters[filter.field] = filter.defaultValue;
        }
      });
      setActiveFilters(prev => ({...prev, ...updatedFilters}));
      onFilterChange?.(updatedFilters);
    }
  }, [enabled, filters, onFilterChange]);

  // Add new state for typeahead
  const [typeaheadField, setTypeaheadField] = useState<string | null>(null);
  const [typeaheadTerm, setTypeaheadTerm] = useState('');

  // Build filter query
  const filterQuery = useMemo(() => {
    const must: any[] = [baseQuery ?? {match_all: {}}];

    Object.entries(activeFilters).forEach(([field, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          // Handle multiple select values
          if (value.length > 0) {
            must.push({
              bool: {
                should: value.map(val => ({
                  match: {[field]: val},
                })),
              },
            });
          }
        } else {
          // Handle single value filters
          must.push({match: {[field]: value}});
        }
      }
    });

    return {bool: {must}};
  }, [activeFilters, baseQuery]);

  // Prepare search request
  const searchRequest: SearchRequest = {
    index: index as Indices,
    query: filterQuery,
    sort: selectedSort ? [{[selectedSort.field]: {order: selectedSort.direction}}] : undefined,
  };

  // Add typeahead query with dynamic aggregation
  const {data: typeaheadData, isLoading: typeaheadLoading} = useElasticQuery({
    table: index,
    searchRequest: searchRequest as any,
    aggs: typeaheadField
      ? {
          typeahead_values: {
            terms: {
              field: filters.find(f => f.field === typeaheadField)?.aggregationField,
              size: 10,
            },
          },
        }
      : undefined,
    enabled: !!(
      typeaheadField && typeaheadTerm.length >= (filters.find(f => f.field === typeaheadField)?.minChars || 2)
    ),
  });

  // Get suggestions from aggregations
  const suggestions = useMemo(() => {
    if (!typeaheadField || !typeaheadData?.aggregations?.typeahead_values) return [];
    return typeaheadData.aggregations.typeahead_values.buckets.map((bucket: {key: string}) => bucket.key);
  }, [typeaheadData, typeaheadField]);

  // Handler for filter changes
  const handleFilterChange = useCallback(
    (field: string, value: any) => {
      setActiveFilters(prev => {
        const newFilters = {
          ...prev,
          [field]: value || undefined, // Remove filter if empty
        };

        // Remove undefined values
        Object.keys(newFilters).forEach(key => {
          if (newFilters[key] === undefined) {
            delete newFilters[key];
          }
        });

        onFilterChange?.(newFilters);
        return newFilters;
      });
      setPage(1); // Reset to first page when filter changes
    },
    [onFilterChange]
  );

  // Data Query
  const {
    data: {data = [], total = 0, aggregations} = {},
    isLoading,
    refetch,
    pagination,
  } = useElasticQuery({
    table: index,
    searchRequest: searchRequest as any,
    page,
    limit,
    searchTerm,
    searchColumn: searchColumn?.toString(),
    enabled,
  });

  // Handlers
  const handleRefresh = useCallback(() => {
    searchInputRef.current.value = '';
    setSearchTerm('');
    refetch();
    onRefresh?.();
  }, [onRefresh, refetch]);

  const handleSort = useCallback(
    (option: ElasticSortOptionT) => {
      setSelectedSort(option);
      onSort?.(option);
      setPage(1);
    },
    [onSort]
  );

  const handleSearchData = useCallback(
    (value: string) => {
      setSearchTerm(value);
      refetch();
    },
    [refetch]
  );

  const handleSetLimit = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newLimit = parseInt(event.target.value, 10) as limitT;
      setLocalLimit(newLimit);
      refetch();
    },
    [refetch]
  );

  const handlePageClick = useCallback(
    (event: ReactPaginateClickEventT) => {
      setPage(event.selected + 1);
      refetch();
    },
    [refetch]
  );

  // Add maxWidthClass for consistent column widths
  const maxWidthClass = 'max-w-xs';

  // Update renderFilterInput to include typeahead
  const renderFilterInput = (filter: ElasticFilterConfig) => {
    switch (filter.type) {
      case 'select':
        return (
          <select
            key={filter.field}
            className="ti-form-control form-select-sm min-w-[160px]"
            onChange={e => handleFilterChange(filter.field, e.target.value)}
            value={activeFilters[filter.field] || ''}
          >
            <option value="">{filter.label}</option>
            {filter.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'text':
      case 'number':
        if (filter.useTypeahead) {
          return (
            <div className="relative">
              <input
                key={filter.field}
                type={filter.type}
                className="ti-form-control form-control-sm min-w-[160px]"
                placeholder={filter.placeholder || filter.label}
                onChange={e => {
                  const value = e.target.value;
                  handleFilterChange(filter.field, value);
                  if (value.length >= (filter.minChars || 2)) {
                    setTypeaheadField(filter.field);
                    setTypeaheadTerm(value);
                  } else {
                    setTypeaheadField(null);
                    setTypeaheadTerm('');
                  }
                }}
                value={activeFilters[filter.field] || ''}
              />
              {typeaheadField === filter.field && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 z-10 mt-1 max-h-60 overflow-auto rounded-md bg-white shadow-lg">
                  {suggestions.map((suggestion: string, index: number) => (
                    <div
                      key={index}
                      className="cursor-pointer bg-black px-4 py-2"
                      onClick={() => {
                        handleFilterChange(filter.field, suggestion);
                        setTypeaheadField(null);
                        setTypeaheadTerm('');
                      }}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }
        return (
          <input
            key={filter.field}
            type={filter.type}
            className="ti-form-control form-control-sm min-w-[160px]"
            placeholder={filter.placeholder || filter.label}
            onChange={e => handleFilterChange(filter.field, e.target.value)}
            value={activeFilters[filter.field] || ''}
          />
        );

      case 'multiple-select':
        return (
          <div className="min-w-[160px]">
            <Select
              isMulti
              className="w-full"
              classNamePrefix="Select2"
              placeholder={filter.placeholder || filter.label}
              options={
                filter.options
                  ? filter.options
                  : (!typeaheadLoading && typeaheadField === filter.field && suggestions ? suggestions : []).map(
                      (suggestion: string) => ({
                        label: suggestion,
                        value: suggestion,
                      })
                    )
              }
              value={
                activeFilters[filter.field]
                  ? (Array.isArray(activeFilters[filter.field])
                      ? activeFilters[filter.field]
                      : [activeFilters[filter.field]]
                    ).map((value: string) => ({
                      label: value,
                      value: value,
                    }))
                  : []
              }
              onFocus={() => {
                if (typeaheadField !== filter.field) {
                  setTypeaheadField(null);
                  setTypeaheadTerm('');
                }
              }}
              onInputChange={value => {
                if (!filter.options && value.length >= (filter.minChars || 2)) {
                  setTypeaheadField(filter.field);
                  setTypeaheadTerm(value);
                } else {
                  setTypeaheadField(null);
                  setTypeaheadTerm('');
                }
              }}
              onChange={selected => {
                const values = selected ? selected.map(option => option.value) : [];
                handleFilterChange(filter.field, values);
              }}
              isSearchable
              menuPlacement="auto"
              isLoading={typeaheadField === filter.field && typeaheadLoading}
              noOptionsMessage={({inputValue}) =>
                inputValue.length < (filter.minChars || 2)
                  ? `Type ${filter.minChars || 2} or more characters to search...`
                  : 'No options found'
              }
            />
          </div>
        );
    }
  };

  // Expose methods via ref
  useImperativeHandle(
    ref,
    () => ({
      refetch: () => {
        if (refetch) refetch();
      },
      setLimit: (newLimit: limitT) => {
        setLocalLimit(newLimit);
      },
      goToPage: (newPage: number) => {
        setPage(newPage);
      },
      nextPage: () => {
        if (page < pagination.totalPages) {
          setPage(page + 1);
        }
      },
      prevPage: () => {
        if (page > 1) {
          setPage(page - 1);
        }
      },
    }),
    [refetch, page, pagination.totalPages]
  );

  return (
    <div className={twMerge('box', className)}>
      {/* Header Section */}
      {!(hideTitle && hideRefresh && hideSort && hideSearch) && (
        <div className="box-header flex-wrap justify-between">
          {!hideTitle && (
            <div className="flex items-center gap-4">
              {typeof title === 'function' ? (
                title({aggregations})
              ) : title ? (
                <h1 className="box-title">{title}</h1>
              ) : null}
            </div>
          )}
          <div className="flex items-stretch gap-2">
            {/* Refresh Button */}
            {!hideRefresh && (
              <button
                onClick={handleRefresh}
                className="ti-btn ti-btn-icon ti-btn-wave !m-0 !h-[1.75rem] !w-[1.75rem] bg-primary/10 text-[0.8rem] text-primary hover:border-primary hover:bg-primary hover:text-white"
              >
                <i className="ri-refresh-line"></i>
              </button>
            )}
            {/* Search Input */}
            {!hideSearch && (
              <div className="me-3">
                <div className="search-form h-full">
                  <input
                    className="ti-form-control form-control-sm h-full"
                    type="text"
                    placeholder="Search Here"
                    aria-label=".form-control-sm example"
                    onChange={() => handleSearchData(searchInputRef.current.value)}
                    ref={searchInputRef}
                  />
                </div>
              </div>
            )}
            {/* Sort Dropdown */}
            {!hideSort && (
              <div className="hs-dropdown ti-dropdown">
                <button className="ti-btn ti-btn-primary !m-0 !gap-0 !bg-primary !px-2 !py-1 !text-[0.75rem] !font-medium !text-white">
                  Sort By <i className="ri-arrow-down-s-line ms-1 inline-block align-middle"></i>
                </button>
                <ul className="hs-dropdown-menu ti-dropdown-menu hidden" role="menu">
                  {sortOptions.map((option, index) => (
                    <li key={index}>
                      <button
                        onClick={() => handleSort(option)}
                        className="ti-dropdown-item !px-[0.9375rem] !py-2 !text-[0.8125rem] !font-medium"
                      >
                        {option.label}
                        {option.field === selectedSort?.field && <i className="ri-check-line ms-2 text-primary"></i>}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Add Button */}
            {!hideAdd && (
              <div>
                <button
                  onClick={onAdd}
                  className="ti-btn ti-btn-primary !m-0 h-full !gap-0 !bg-primary !px-2 !py-1 !text-[0.75rem] !font-medium !text-white"
                >
                  {addButtonLabel}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters Section */}
      {filters.length > 0 && (
        <div className="box-body border-b pb-4">
          <div className="flex flex-wrap items-center gap-3">
            {filters.map(filter => (
              <div key={filter.field} className="flex-shrink-0">
                {renderFilterInput(filter)}
              </div>
            ))}

            {Object.keys(activeFilters).length > 0 && (
              <button
                className="ti-btn ti-btn-md ti-btn-danger"
                onClick={() => {
                  setActiveFilters({});
                  onFilterChange?.({});
                }}
              >
                <i className="ri-close-line me-1"></i>
                Clear Filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table Body */}
      <div className="box-body">
        {isLoading && !hideLoading ? (
          <LoadingSpinningLogoComponent />
        ) : (
          <div className="table-responsive">
            <table className="table-hover table-bordered table-sm ti-striped-table table min-w-full whitespace-nowrap">
              <thead>
                <tr>
                  {actions && <th className="!text-end">Actions</th>}
                  {columns.map((column, index) => (
                    <th key={index} className="text-start">
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr className="border">
                    <td colSpan={columns.length + (actions ? 1 : 0)}>
                      <div className="text-center font-bold">{noResultsMessage}</div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border border-solid border-inherit hover:bg-gray-100 dark:border-defaultborder/10 dark:hover:bg-light"
                    >
                      {actions && (
                        <td>
                          <div className="flex flex-row items-center justify-center gap-2 text-[0.9375rem]">
                            {actions
                              .filter(action => action.isShown?.(row._source) ?? true)
                              .map(
                                (action, actionIndex) =>
                                  action.permission(row._source) && (
                                    <button
                                      key={actionIndex}
                                      onClick={() => action.handler(row._source)}
                                      className={`ti-btn ti-btn-icon ti-btn-wave !m-0 !h-[1.75rem] !w-[1.75rem] !gap-0 text-[0.8rem] bg-${action.color}/10 text-${action.color} hover:bg-${action.color} hover:text-white hover:border-${action.color}`}
                                    >
                                      <i className={action.icon}></i>
                                    </button>
                                  )
                              )}
                          </div>
                        </td>
                      )}
                      {columns.map((column, colIndex) => (
                        <td className={`overflow-hidden ${maxWidthClass}`} key={colIndex}>
                          {column.render
                            ? column.render(row._source)
                            : String(getNestedValue(row._source, column.field as string) ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer Section */}
      <div className="box-footer">
        <div className="flex items-center justify-between">
          {!(hideShowing && hideLimitIcon && hideLimit) && (
            <div className="flex items-center text-sm text-gray-700 dark:text-gray-400">
              {!hideShowing && (
                <div>
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
                </div>
              )}
              {!hideLimitIcon && <i className="bi bi-arrow-right ms-2 font-semibold"></i>}
              {!hideLimit && (
                <select
                  className="ti-form-control form-select-sm form-select ml-4"
                  value={limit}
                  onChange={handleSetLimit}
                >
                  {VALID_LIMITS.map((value, index) => (
                    <option key={index} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          {!hidePagination && pagination.totalPages > 1 && (
            <div className="pagination-style-4">
              <ReactPaginate
                forcePage={page - 1}
                onClick={handlePageClick}
                pageCount={Math.max(1, pagination.totalPages)}
                pageRangeDisplayed={3}
                marginPagesDisplayed={2}
                renderOnZeroPageCount={null}
                className="ti-pagination select-none gap-1"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                activeLinkClassName="active"
                breakLabel={'...'}
                breakLinkClassName="page-item"
                nextClassName={clsx({'hidden disabled': page === pagination.totalPages})}
                nextLinkClassName="page-link"
                nextLabel={'Next'}
                previousClassName={clsx({'hidden disabled': page === 1})}
                previousLinkClassName="page-link"
                previousLabel={'Prev'}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Define a proper generic forwardRef type
type ForwardRefComponent<P, R> = React.ForwardRefExoticComponent<React.PropsWithoutRef<P> & React.RefAttributes<R>>;

// Create the component with the correct type
export const ElasticDataTable: ForwardRefComponent<
  ElasticDataTablePropsT<ElasticTable>,
  ElasticDataTableRefT
> = React.forwardRef(
  <T extends ElasticTable>(props: ElasticDataTablePropsT<T>, ref: React.ForwardedRef<ElasticDataTableRefT>) =>
    ElasticDataTableComponent(props, ref)
);

// Now we can safely set the displayName
ElasticDataTable.displayName = 'ElasticDataTable';
